import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceCard from "../components/ServiceCard";
import UserReviewCard from "../components/UserReviewCard";
import { Container, Box, Typography, Button, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid2";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ExploreAurangabadSection from "../components/ExploreAurangabadSection";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { motion } from 'framer-motion';


function HomePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Handle scroll for parallax effects
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };
  
  const floatAnimation = {
    initial: { y: 0 },
    animate: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }
    }
  };

  return (
    <div style={{ overflow: "hidden" }}>
      <div style={{ 
        position: "fixed", 
        width: "100%", 
        height: "100vh", 
        zIndex: -1,
        backgroundImage: "url('/images/world-map-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.05
      }} />
      
      <Header />
      
      {/* Hero Section with Parallax */}
      <Box sx={{ 
        position: "relative", 
        height: isMobile ? "85vh" : "100vh",
        background: "linear-gradient(180deg, rgba(219,53,42,0.05) 0%, rgba(255,255,255,1) 100%)",
        overflow: "hidden"
      }}>
        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          style={{
            position: "absolute",
            right: isMobile ? "-100px" : "50px",
            top: "20%",
            zIndex: 1
          }}
        >
          <motion.img
            animate={{ 
              y: [0, -15, 0],
              rotate: [0, 5, 0]
            }}
            transition={{ 
              duration: 6, 
              repeat: Infinity,
              repeatType: "reverse"
            }}
            src="illustrations/Young man with a backpack and items for traveling around.png"
            alt="Young man with a backpack"
            style={{
              width: isMobile ? "180px" : "350px",
              height: "auto",
              filter: "drop-shadow(0px 10px 15px rgba(0,0,0,0.15))"
            }}
          />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          style={{
            position: "absolute",
            left: isMobile ? "-70px" : "50px",
            top: "30%",
            zIndex: 1
          }}
        >
          <motion.img
            animate={{ 
              y: [0, 15, 0],
              rotate: [0, -5, 0]
            }}
            transition={{ 
              duration: 5, 
              repeat: Infinity,
              repeatType: "reverse"
            }}
            src="illustrations/Passport and plane tickets for travelling abroad.png"
            alt="Passport and plane tickets"
            style={{
              width: isMobile ? "150px" : "250px",
              height: "auto",
              filter: "drop-shadow(0px 10px 15px rgba(0,0,0,0.15))"
            }}
          />
        </motion.div>
        
        {/* Hero Content */}
        <Container maxWidth="lg">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "80vh",
              position: "relative",
              zIndex: 2
            }}
          >
            <Box sx={{
              display: "flex", 
              alignItems: "center",
              mb: 2
            }}>
              <motion.div variants={fadeIn}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    mr: 1
                  }}
                >
                  Recommended By 
                </Typography>
              </motion.div>
              
              <motion.div 
                variants={fadeIn}
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/images/LovelyPlanetLogo (2).png`}
                  alt="Lovely Planet logo"
                  sx={{
                    height: { xs: 70, sm: 100 },
                    width: { xs: 120, sm: 170 },
                  }}
                />
              </motion.div>
            </Box>
            
            <motion.div variants={fadeIn}>
              <Typography
                variant="h1"
                color="#db352a"
                sx={{ 
                  fontWeight: 700, 
                  textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
                  fontSize: { xs: "3rem", sm: "4.5rem" }
                }}
              >
                Ashoka
              </Typography>
            </motion.div>
            
            <motion.div variants={fadeIn}>
              <Typography
                variant="h6"
                color="#db352a"
                sx={{ 
                  fontStyle: "italic",
                  letterSpacing: "0.1em",
                  mb: 4,
                  textAlign: "right",
                  ml: { xs: 0, sm: 16 },
                  mt: -1
                }}
              >
                Tours and Travel
              </Typography>
            </motion.div>

            <motion.div 
              variants={fadeIn}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                color="error"
                size={isMobile ? "medium" : "large"}
                sx={{ 
                  mt: 4, 
                  bgcolor: "#db352a",
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  boxShadow: "0 8px 16px rgba(219,53,42,0.3)",
                  fontWeight: "bold",
                  fontSize: "1.1rem"
                }}
                component={Link}
                to="/booking"
                startIcon={<FlightTakeoffIcon />}
              >
                Book Your Adventure
              </Button>
            </motion.div>

            {/* Contact Information */}
            <motion.div 
              variants={fadeIn}
              style={{ width: "100%", marginTop: "2rem" }}
            >
              <Box sx={{ 
                mt: 6, 
                display: "flex", 
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                gap: 2
              }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<PhoneIcon sx={{ color: "#db352a" }} />}
                  sx={{ 
                    borderRadius: "50px",
                    borderWidth: "2px",
                    borderColor: "rgba(219,53,42,0.5)",
                    "&:hover": {
                      borderColor: "#db352a",
                      backgroundColor: "rgba(219,53,42,0.05)"
                    }
                  }}
                  href="tel:+919890565100"
                  onClick={() => window.open("tel:+919890565100", "_self")}
                >
                  <Typography variant="body1" color="#db352a" fontWeight="medium">
                    +91 9890565100
                  </Typography>
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<PhoneIcon sx={{ color: "#db352a" }} />}
                  sx={{ 
                    borderRadius: "50px",
                    borderWidth: "2px",
                    borderColor: "rgba(219,53,42,0.5)",
                    "&:hover": {
                      borderColor: "#db352a",
                      backgroundColor: "rgba(219,53,42,0.05)"
                    }
                  }}
                  href="tel:+919890340816"
                  onClick={() => window.open("tel:+919890340816", "_self")}
                >
                  <Typography variant="body1" color="#db352a" fontWeight="medium">
                    +91 9890340816
                  </Typography>
                </Button>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  startIcon={<EmailIcon sx={{ color: "#db352a" }} />}
                  sx={{ 
                    borderRadius: "50px",
                    borderWidth: "2px",
                    borderColor: "rgba(219,53,42,0.5)",
                    "&:hover": {
                      borderColor: "#db352a",
                      backgroundColor: "rgba(219,53,42,0.05)"
                    }
                  }}
                  href="mailto:atkadam88@gmail.com"
                  target="_blank"
                >
                  <Typography variant="body1" color="#db352a" fontWeight="medium">
                    atkadam88@gmail.com
                  </Typography>
                </Button>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Explore Aurangabad Section */}
      <ExploreAurangabadSection />

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ mt: 10, position: "relative", overflow: "hidden" }}>
        {/* Decorative floating element */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            position: "absolute",
            right: isMobile ? "-100px" : "-50px",
            top: "50%",
            zIndex: 0
          }}
        >
          <motion.img
            animate={floatAnimation.animate}
            transition={floatAnimation.transition}
            src="illustrations/Young male traveler with backpack.png"
            alt="Young male traveler with backpack"
            style={{
              width: isMobile ? "180px" : "300px",
              height: "auto",
              opacity: 0.8
            }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ py: 6 }}>
            <Typography
              variant="h3"
              component="h2"
              color="#db352a"
              align="center"
              sx={{ 
                textAlign: "center", 
                fontWeight: "bold",
                position: "relative",
                display: "inline-block",
                left: "50%",
                transform: "translateX(-50%)",
                mb: 2,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "50%",
                  height: "4px",
                  borderRadius: "2px",
                  backgroundColor: "#db352a",
                  bottom: "-10px",
                  left: "25%"
                }
              }}
            >
              Our Services
            </Typography>
          </Box>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "center",
              alignItems: { xs: "center", md: "stretch" },
              gap: 4,
              my: 6,
              py: 2,
              position: "relative",
              zIndex: 1
            }}
          >
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ServiceCard
                serviceTitle="Hotel Booking"
                imagePath={process.env.PUBLIC_URL + "/images/hotel1.jpg"}
                animation={true}
              />
            </motion.div>
            
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ServiceCard
                serviceTitle="Hire Guide"
                imagePath={process.env.PUBLIC_URL + "/images/GuideMap.jpg"}
                animation={true}
              />
            </motion.div>
            
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <ServiceCard
                serviceTitle="Currency Exchange"
                imagePath={process.env.PUBLIC_URL + "/images/CurrencyExchange.png"}
                animation={true}
              />
            </motion.div>
          </Box>
        </motion.div>
      </Container>

      {/* Reviews Section */}
      <Box
        sx={{
          width: "100%",
          height: { xs: "auto", md: "auto" },
          background: "linear-gradient(135deg, #db352a 0%, #ff7366 100%)",
          mt: 8,
          pt: 6,
          pb: 10,
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Background decorative elements */}
        <Box sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "url('/images/world-map-dots.png')",
          backgroundSize: "cover",
          opacity: 0.1,
          zIndex: 0
        }} />
        
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              component="h2"
              color="white"
              sx={{ 
                textAlign: "center",
                fontWeight: "bold",
                mb: 8,
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "60px",
                  height: "4px",
                  backgroundColor: "white",
                  bottom: "-15px",
                  left: "50%",
                  transform: "translateX(-50%)"
                }
              }}
            >
              What Our Travelers Say
            </Typography>
          </motion.div>
          
          <Grid
            container
            spacing={4}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            {[
              {
                review: '"I had a great experience with Ashokatours. The hotel booking service was excellent and the guide was very knowledgeable about the places we visited."',
                userName: "John Doe"
              },
              {
                review: '"This was the second time we have used Ashoka Car facility and as before all went well. Pick up and drop off timely and efficient. Car was well worth the hire fees and the the best compliment is we will use Ashoka next time also."',
                userName: "SK Ansari"
              },
              {
                review: '"People very responsible and nice. Always willing to help and giving their best. Administrative staff is very serious and giving always very good options. I use to arrange the vehicles of this agency for my groups coming to India and they are always very happy!"',
                userName: "Merlin"
              },
              {
                review: '"Transportation was on time, drivers were punctual and very polite to accommodate waiting time. mini bus was in great condition and perfect for the guests. you have been so quick to respond, professional, helpful and incredibly friendly."',
                userName: "Priyanka C."
              }
            ].map((item, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <UserReviewCard
                    review={item.review}
                    userName={item.userName}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Address Section */}
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              textAlign: "center",
              color: "#db352a",
              mt: 10,
              mb: 6,
              fontWeight: "bold",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                width: "60px",
                height: "4px",
                backgroundColor: "#db352a",
                bottom: "-15px",
                left: "50%",
                transform: "translateX(-50%)"
              }
            }}
          >
            Find Us
          </Typography>
        </motion.div>
        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "center" },
            justifyContent: "space-between",
            my: 6,
            pb: 6
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            style={{ width: isMobile ? "100%" : "45%" }}
          >
            <Box sx={{ 
              p: 4, 
              borderRadius: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              background: "white"
            }}>
              <Typography
                variant="h5"
                gutterBottom
                color="#db352a"
                sx={{ mb: 3, fontWeight: "medium" }}
              >
                Visit Our Office
              </Typography>
              
              <Typography
                variant="body1"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 3,
                  lineHeight: 1.6
                }}
              >
                <LocationOnIcon
                  sx={{
                    mr: 2,
                    fontSize: "1.5rem",
                    color: "#db352a",
                    mt: 0.5
                  }}
                />
                Hotel Raviraj, Dr Rajendra Prasad Marg, Adalat Rd, near Baba Petrol Pump, Chhatrapati Sambhaji Nagar, Maharashtra 431005
              </Typography>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="contained"
                  color="error"
                  size="medium"
                  sx={{ 
                    mt: 2,
                    borderRadius: "50px",
                    px: 3,
                    py: 1,
                    bgcolor: "#db352a",
                    fontWeight: "medium"
                  }}
                  onClick={() =>
                    window.open(
                      "https://maps.app.goo.gl/C8gMwQ2mJKwK34Ao8",
                      "_blank"
                    )
                  }
                >
                  Get Directions
                </Button>
              </motion.div>
            </Box>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            style={{ width: isMobile ? "100%" : "50%", marginTop: isMobile ? "2rem" : 0 }}
          >
            <Box
  sx={{
    height: "100%",
    width: "100%",
    borderRadius: "10px",
    boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
    overflow: "hidden"
  }}
>
  <iframe
    title="Location Map"
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3625.169400010629!2d75.3164881!3d19.873905999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdb99004e713f8f%3A0x23c552c129301c39!2sAshoka%20Tours%20and%20Travels!5e1!3m2!1sen!2sin!4v1762587953591!5m2!1sen!2sin"
    width="100%"
    height="450"
    style={{ border: 0 }}
    allowFullScreen=""
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
  />
</Box>

          </motion.div>
        </Box>
      </Container>
      
      <Footer />
    </div>
  );
}

export default HomePage;